import { z } from 'zod';

import {
  CARD_STATE,
  COMMAND_TYPE,
  DEVICE_POLICY,
  DEVICE_STATUS,
  EFFECTIVE_STATUSES,
  SUBSCRIPTION_STATUS,
} from './constants';

// Two layers on purpose:
//
//   * the `*Wire` schemas describe exactly what the backend sends — confirmed against the live
//     API by the Tracking Mobile probes (see
//     greenway_mobile/docs/specs/features/devices/device-listing/api-contract.md);
//   * `Device` is the normalised shape the components consume.
//
// `toDevice` is the one place the two meet. Keeping them apart means a backend addition or a
// spelling change lands in one function instead of rippling through the UI, and it lets the
// parsing be *tolerant* — the list must not blank out because one row grew a field this app
// doesn't know about yet.

export const deviceStatusSchema = z.enum([
  DEVICE_STATUS.RUNNING,
  DEVICE_STATUS.OFF,
  DEVICE_STATUS.DISCONNECTED,
  DEVICE_STATUS.EXPIRED,
  DEVICE_STATUS.PENDING_QC,
  DEVICE_STATUS.PENDING_CONFIGURATION,
]);

export const subscriptionStatusSchema = z.enum([
  SUBSCRIPTION_STATUS.ACTIVE,
  SUBSCRIPTION_STATUS.EXPIRED,
]);

export const commandTypeSchema = z.enum([COMMAND_TYPE.TURN_ON, COMMAND_TYPE.TURN_OFF]);

/**
 * `effectiveStatus` off the wire. An unrecognised value must not crash the list — the backend
 * can add a status before this app knows about it — so it degrades to Pending Configuration,
 * the most conservative (fully disabled) state.
 */
const effectiveStatusWire = z.enum(EFFECTIVE_STATUSES).catch(DEVICE_STATUS.PENDING_CONFIGURATION);

/** `cardState`; anything that isn't explicitly `disabled` is treated as enabled. */
const cardStateWire = z.enum([CARD_STATE.ENABLED, CARD_STATE.DISABLED]).catch(CARD_STATE.ENABLED);

/**
 * A command value, accepting every spelling seen or plausible on this endpoint.
 *
 * `queuedAction`'s shape is the one genuinely unverified part of the confirmed contract — every
 * probed device returned `null` and the OpenAPI spec publishes no schema — so this mirrors
 * Tracking Mobile's tolerance rather than guessing one shape and breaking on another.
 */
const commandValueWire = z
  .union([z.literal('turn_on'), z.literal('TURN_ON'), z.literal('turnOn')])
  .transform(() => COMMAND_TYPE.TURN_ON)
  .or(
    z
      .union([z.literal('turn_off'), z.literal('TURN_OFF'), z.literal('turnOff')])
      .transform(() => COMMAND_TYPE.TURN_OFF),
  );

/** `queuedAction`: a bare command string, or an object also carrying the id needed to cancel. */
const queuedActionWire = z
  .union([
    commandValueWire.transform((type) => ({ type, commandId: null as string | null })),
    z
      .object({
        type: commandValueWire.optional(),
        action: commandValueWire.optional(),
        commandId: z.string().optional(),
        id: z.string().optional(),
      })
      .transform((raw) => {
        const type = raw.type ?? raw.action;
        return type ? { type, commandId: raw.commandId ?? raw.id ?? null } : null;
      }),
  ])
  .nullish()
  .catch(null);

export const deviceFieldWireSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
});

export const deviceSubscriptionWireSchema = z.object({
  status: subscriptionStatusSchema.catch(SUBSCRIPTION_STATUS.ACTIVE),
  expiresAt: z.string().nullish(),
});

export const devicePowerIconWireSchema = z.object({
  visible: z.boolean().catch(false),
  enabled: z.boolean().catch(false),
  /** Null when no command is legal right now. Also reveals the live state: turn_off ⇒ running. */
  action: commandValueWire.nullish().catch(null),
});

/** One entry of `data.devices` from `GET /devices/tracking/list`. */
export const deviceWireSchema = z.object({
  id: z.string().catch(''),
  /** The manufacturer/default name. */
  deviceName: z.string().nullish(),
  /** Grower-assigned nickname. Search matches this OR `deviceName`. */
  customName: z.string().nullish(),
  /** A free-form category *name*, e.g. "Moisture Sensors" — not a diesel/electric/sensor enum. */
  deviceType: z.string().nullish(),
  lastActiveAt: z.string().nullish(),
  effectiveStatus: effectiveStatusWire,
  /** A single object or `null` — the API associates a device with **at most one** field. */
  field: deviceFieldWireSchema.nullish(),
  subscription: deviceSubscriptionWireSchema.nullish(),
  cardState: cardStateWire,
  powerIcon: devicePowerIconWireSchema.nullish(),
  queuedAction: queuedActionWire,
});

export type DeviceWire = z.infer<typeof deviceWireSchema>;

/** Cursor page envelope: `{ devices, meta: { hasNextPage, nextCursor } }` under `data`. */
export const devicePageWireSchema = z.object({
  devices: z.array(deviceWireSchema).catch([]),
  meta: z
    .object({
      hasNextPage: z.boolean().catch(false),
      nextCursor: z.string().nullish(),
    })
    .catch({ hasNextPage: false, nextCursor: null }),
});

export const deviceCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type DeviceCategory = z.infer<typeof deviceCategorySchema>;

export const deviceCategoryListSchema = z.array(deviceCategorySchema).catch([]);

// ── Normalised domain shape ───────────────────────────────────────────────────────────────────

export type DeviceField = { id: string | null; name: string };

export type QueuedCommand = {
  type: (typeof COMMAND_TYPE)[keyof typeof COMMAND_TYPE];
  /** Needed to cancel: `DELETE .../commands/{commandId}`. Null when the server omitted it. */
  commandId: string | null;
};

export type PowerIcon = {
  visible: boolean;
  enabled: boolean;
  action: QueuedCommand['type'] | null;
};

export type Device = {
  id: string;
  /** Default/manufacturer name — the card headline. */
  name: string;
  /** Grower nickname, or null. Leads the details header when present. */
  customName: string | null;
  /** Category name shown above the device name on the card. */
  category: string;
  /** `effectiveStatus`, with an expired subscription folded in as `expired`. */
  status: (typeof DEVICE_STATUS)[keyof typeof DEVICE_STATUS];
  lastSyncedAt: string | null;
  subscriptionStatus: (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];
  subscriptionExpiresAt: string | null;
  /**
   * 0-or-1 entries. The API returns `field: {id, name} | null`, so a device has **at most one**
   * field; this stays a list only because the card renders a chip row.
   */
  fields: DeviceField[];
  queuedCommand: QueuedCommand | null;
  /** Server-computed. Null only for devices built without a server payload (tests, fixtures). */
  powerIcon: PowerIcon | null;
  cardDisabled: boolean;
};

/**
 * Normalise one wire row.
 *
 * An expired subscription outranks the device's own status for display: the backend never sends
 * `effectiveStatus: 'expired'`, yet every surface renders such a device as "Expired".
 */
export function toDevice(wire: DeviceWire): Device {
  const subscriptionStatus = wire.subscription?.status ?? SUBSCRIPTION_STATUS.ACTIVE;
  const fieldName = wire.field?.name;

  return {
    id: wire.id,
    name: wire.deviceName ?? '',
    customName: wire.customName ?? null,
    category: wire.deviceType ?? '',
    status:
      subscriptionStatus === SUBSCRIPTION_STATUS.EXPIRED
        ? DEVICE_STATUS.EXPIRED
        : wire.effectiveStatus,
    lastSyncedAt: wire.lastActiveAt ?? null,
    subscriptionStatus,
    subscriptionExpiresAt: wire.subscription?.expiresAt ?? null,
    fields: fieldName ? [{ id: wire.field?.id ?? null, name: fieldName }] : [],
    queuedCommand: wire.queuedAction ?? null,
    powerIcon: wire.powerIcon
      ? {
          visible: wire.powerIcon.visible,
          enabled: wire.powerIcon.enabled,
          action: wire.powerIcon.action ?? null,
        }
      : null,
    cardDisabled: wire.cardState === CARD_STATE.DISABLED,
  };
}

export type DevicePage = {
  devices: Device[];
  hasNextPage: boolean;
  nextCursor: string | null;
};

/** Query the listing sends. Every filter is applied by the backend. */
export type DeviceListQuery = {
  limit: number;
  cursor: string | null;
  search: string;
  /** Category **id** (uuid) from `meta/categories`, or null for "All". */
  categoryId: string | null;
  deviceStatus: Device['status'][];
  subscriptionStatus: Device['subscriptionStatus'][];
  queuedOnly: boolean;
};

/** Rename Device form (Figma nodes 475:417006 / 475:421684). */
export const renameDeviceSchema = z.object({
  customName: z
    .string()
    .trim()
    .min(
      DEVICE_POLICY.NAME_MIN_LENGTH,
      `Please enter a valid name (${DEVICE_POLICY.NAME_MIN_LENGTH} - ${DEVICE_POLICY.NAME_MAX_LENGTH}) characters.`,
    )
    .max(
      DEVICE_POLICY.NAME_MAX_LENGTH,
      `Please enter a valid name (${DEVICE_POLICY.NAME_MIN_LENGTH} - ${DEVICE_POLICY.NAME_MAX_LENGTH}) characters.`,
    ),
});

export type RenameDeviceInput = z.infer<typeof renameDeviceSchema>;
